import { Grid2, Stack, styled, Switch, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import Heading from "./Heading";
import { CommonFunctions } from "../utils/CommonFunctions";
import { HtmlHelpers } from "../constants/all-constant";

export const NewOffsetInput = ({
  isSingleLineLayout,
  onValueChanged,
  value,
  errorMessage,
  isRequired = false,
  onlyHighlightOnError = false,
  disabled = false,
  width = undefined,
}: {
  isSingleLineLayout?: boolean;
  onValueChanged(valueInMinutes: number | undefined): void;
  value: number | undefined;
  errorMessage: string;
  isRequired?: boolean;
  onlyHighlightOnError?: boolean;
  disabled?: boolean;
  width?: string;
}) => {
  const maxDaysAllowed = 90;
  const maxHoursAllowed = 23;

  const OnOffSwitch = styled(Switch)(({ theme }) => ({
    width: 24,
    height: 14,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 10,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(10px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 1.5,
      "&.Mui-checked": {
        transform: "translateX(10px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          border: "2px solid var(--color-primary-main)",
          backgroundColor: "white",
        },
        "& .MuiSwitch-thumb": {
          background: "var(--color-primary-main)",
        },
      },
      "&.Mui-disabled": {
        color: theme.palette.action.disabled,
        "& .MuiSwitch-thumb": {
          background: theme.palette.action.disabled,
        },
        "& + .MuiSwitch-track": {
          backgroundColor: theme.palette.action.disabledBackground,
          border: "2px solid transparent",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: 7,
      height: 7,
      background: "black",
      borderRadius: 7,
      margin: "2px",
      transition: theme.transitions.create(["width"], {
        duration: 1000,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 8,
      opacity: 1,
      border: "2px solid black",
      backgroundColor: "white",
      boxSizing: "border-box",
    },
  }));

  const [isNegative, setIsNegative] = useState(
    (value ?? 0) >= 0 ? false : true
  );
  const [negativeText, setNegativeText] = useState(
    (value ?? 0) >= 0 ? "Positive" : "Negative"
  );
  let inputOffset = CommonFunctions.convertMinutesToOffsetObject(value);
  const [offsetDays, setOffsetDays] = useState<string>(
    inputOffset.day > 0
      ? inputOffset.day.toString()
      : inputOffset.day === 0
      ? ""
      : ""
  );
  const [offsetHours, setOffsetHours] = useState<string>(
    inputOffset.hour > 0
      ? inputOffset.hour.toString()
      : inputOffset.hour === 0
      ? ""
      : ""
  );

  const isValidNumberInput = (value: string) => {
    return !(
      value.includes(".") ||
      value.includes("-") ||
      value.includes("+") ||
      value.length > 2
    );
  };

  useEffect(() => {
    inputOffset = CommonFunctions.convertMinutesToOffsetObject(value);
    setOffsetDays(
      inputOffset.day > 0
        ? inputOffset.day.toString()
        : inputOffset.day === 0
        ? ""
        : ""
    );
    setOffsetHours(
      inputOffset.hour > 0
        ? inputOffset.hour.toString()
        : inputOffset.hour === 0
        ? ""
        : ""
    );
    if (typeof value === "number") {
      setIsNegative(
        value === 0 || value === -0 ? isNegative : value < 0 ? true : false
      );
    } else {
      setIsNegative(false);
    }
  }, [value]);

  useEffect(() => {
    setNegativeText(isNegative ? "Negative" : "Positive");
  }, [isNegative]);

  const handleAnyChange = (
    isNegativeValue: boolean,
    offsetDaysValue: string,
    offsetHoursValue: string
  ) => {
    if (offsetDaysValue === "" && offsetHoursValue === "") {
      onValueChanged(undefined);
    } else {
      const totalMins = CommonFunctions.convertOffsetToMinutes(
        (isNegativeValue ? "-" : "") +
          Number(offsetDaysValue) +
          "D " +
          Number(offsetHoursValue) +
          "H"
      );
      onValueChanged(totalMins);
    }
  };

  const offsetLabel = (isBigger: boolean) => {
    return (
      <Heading
        type="body2"
        text="Offset:"
        isBold={true}
        font={isBigger ? "16px" : ""}
        style={disabled ? { color: "rgba(0, 0, 0, 0.38" } : {}}
      />
    );
  };

  const onOffSwitchComponent = () => {
    return (
      <OnOffSwitch
        checked={isNegative}
        onChange={(e, checked) => {
          setIsNegative(checked);
          handleAnyChange(checked, offsetDays, offsetHours);
        }}
        sx={{ marginRight: "5px" }}
        disabled={disabled}
      />
    );
  };

  const negativeTextLabel = () => {
    return (
      <Heading
        type="body2"
        text={negativeText}
        font="12px"
        color={disabled ? "rgba(0,0, 0, 0.38)" : "inherit"}
      />
    );
  };
  const disabledTextFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "&.Mui-disabled": {
        "&:hover": {
          borderColor: "transparent", // Remove hover border when disabled
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)", // Set a subtle border color when disabled
        },
      },
    },
  };

  const daysTextBox = () => {
    return (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        type="text"
        label="Days"
        name="offsetDays"
        required={isRequired}
        value={offsetDays}
        onChange={(e) => {
          if (!isValidNumberInput(e.target.value)) return;
          if (Number(e.target.value) <= maxDaysAllowed) {
            setOffsetDays(e.target.value);
            handleAnyChange(isNegative, e.target.value, offsetHours);
          }
        }}
        error={!!errorMessage}
        sx={{
          marginRight: "10px",
          ...disabledTextFieldStyle,
          "& .MuiInputLabel-root": {
            color: disabled ? "rgba(0, 0, 0, 0.38)" : "inherit", // Disable label color
          },
          // color:
          //   !!error
          //     ? "var(--color-error-main) !important"
          //     : "",
        }}
        disabled={disabled}
      />
    );
  };

  const hoursTextBox = () => {
    return (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        required={isRequired}
        type="text"
        label="Hours"
        name="offsetHours"
        value={offsetHours}
        onChange={(e) => {
          if (!isValidNumberInput(e.target.value)) return;
          if (Number(e.target.value) <= maxHoursAllowed) {
            setOffsetHours(e.target.value);
            handleAnyChange(isNegative, offsetDays, e.target.value);
          }
        }}
        error={!!errorMessage}
        sx={{
          marginRight: "10px",
          ...disabledTextFieldStyle,
          "& .MuiInputLabel-root": {
            color: disabled ? "rgba(0, 0, 0, 0.38)" : "inherit", // Disable label color
          },
          // color:
          //   isSubmitted && !!error
          //     ? "var(--color-error-main) !important"
          //     : "",
        }}
        disabled={disabled}
      />
    );
  };

  const errorLabel = (styles = {}) => {
    if (!onlyHighlightOnError) {
      return (
        <span
          style={{
            marginTop: "-2px",
            fontSize: ".8rem",
            color: "var(--color-error-main)",
            fontWeight: "normal",
            ...styles,
          }}
        >
          {errorMessage}
        </span>
      );
    }
  };

  return isSingleLineLayout ? (
    <React.Fragment>
      <Stack direction="column" spacing={0.25} width={width}>
        <Stack direction="row">
          <Stack
            direction="column"
            sx={{ marginRight: "10px", width: "222px" }}
          >
            {offsetLabel(true)}
            <Stack direction="row" sx={{ alignItems: "center" }}>
              {onOffSwitchComponent()}
              {negativeTextLabel()}
            </Stack>
          </Stack>
          {daysTextBox()}
          {hoursTextBox()}
        </Stack>
        {errorLabel({ marginLeft: "90px", marginTop: "0" })}
      </Stack>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Stack
        direction="row"
        spacing={0.25}
        width={width}
        sx={{ alignItems: "center", paddingLeft: "5px" }}
      >
        {offsetLabel(false)}
        {HtmlHelpers.DOUBLE_SPACE}
        {onOffSwitchComponent()}
        {HtmlHelpers.DOUBLE_SPACE}
        {negativeTextLabel()}
      </Stack>
      <Grid2 width={width} container spacing={1} style={{ marginTop: "5px" }}>
        <Grid2 size={6}>{daysTextBox()}</Grid2>
        <Grid2 size={6}>{hoursTextBox()}</Grid2>
        {errorLabel()}
      </Grid2>
    </React.Fragment>
  );
};
